import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { resolveOrgResumedOwner, sameOrgUnit } from "@/lib/org-resume";
import {
  otherConversationOpenText,
  toastOpenError,
} from "@/lib/conversation-refusal";
import { effectiveOrgId } from "@/lib/org-structure-tree";
import type { OrgUnitRow } from "@/api/management";
import { getOrgUnitContext, type OrgRunKey } from "@/api/org-execution";
import { openOrgUnitConversation } from "@/api/stage-conversations";
import { useCbcConnection } from "@/hooks/useConfigMigration";
import { useOrgUnitsScroll } from "@/hooks/useManagementLists";
import { useDebouncedSearch } from "@/hooks/useDebouncedSearch";
import { orgNodeFromRow, orgNodeFromContext } from "@/lib/org-unit-row";
import {
  orgUnitTypeLabelKey,
  type OrgTreeNode,
} from "@/lib/org-structure-tree";
import type {
  ScriptModeResolver,
  RunQueueState,
} from "@/components/agents/configuration-server/OrgStructureTree";
import { OrgNodeDetailDrawer } from "@/components/agents/configuration-server/OrgNodeDetailDrawer";
import {
  ExecutionListCard,
  ExecutionLine,
  RunAllButton,
  type ExecutionListRow,
  type ExecutionRowStatus,
} from "./ExecutionListCard";
import { LoadMore } from "./Paging";
import { StageLoading } from "./StageLoading";
import { CbcConnectionDialog } from "./ConnectionDialogs";
export interface OrgExecutionTreeProps {
  projectId: string;
  live?: boolean;
  executionOnly?: boolean;
  hideExecution?: boolean;
  view?: string;
  waitingRunIds?: ReadonlySet<string>;
  activeNodeKey?: string | null;
  counts?: { approved: number; completed: number };
  statusByKey?: ReadonlyMap<string, OrgRunKey>;
  onSelectNodeOverride?: (node: OrgTreeNode) => void;
  onRunStart?: (node: OrgTreeNode) => void;
  onConversationOpened?: (runId: string, node: OrgTreeNode) => void;
  onRunAll?: (nodes: OrgTreeNode[]) => void;
  runQueue?: RunQueueState | null;
  onStopQueue?: () => void;
  onRunStateChange?: () => void;
  resolveScriptMode?: ScriptModeResolver;
  runBlockedReason?: string | null;
  onCanonicalNodesChange?: (nodes: OrgTreeNode[]) => void;
}
export function OrgExecutionTree(props: OrgExecutionTreeProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [q, commitSearch] = useDebouncedSearch(search);
  const roots = useOrgUnitsScroll(props.projectId, undefined, {
    verdict: "approved",
    q,
  });
  const [children, setChildren] = useState<Record<string, OrgUnitRow[]>>({});
  const register = useCallback(
    (parent: string, rows: OrgUnitRow[]) =>
      setChildren((current) =>
        current[parent] === rows ? current : { ...current, [parent]: rows },
      ),
    [],
  );
  useEffect(() => {
    setChildren({});
  }, [q]);
  const rootRows = useMemo(
    () => roots.data?.pages.flatMap((page) => page.items) ?? [],
    [roots.data],
  );
  const canonical = useMemo(
    () =>
      [
        ...new Map(
          [...rootRows, ...Object.values(children).flat()].map((row) => [
            row.key,
            row,
          ]),
        ).values(),
      ].map((row) => {
        const status = props.statusByKey?.get(row.key);
        return {
          ...orgNodeFromRow(row),
          migrationStatus: (status?.status ??
            row.migration_status ??
            "pending") as OrgTreeNode["migrationStatus"],
          lastRunId: status?.last_run_id ?? row.last_run_id ?? null,
        };
      }),
    [rootRows, children, props.statusByKey],
  );
  const { onCanonicalNodesChange } = props;
  useEffect(() => {
    onCanonicalNodesChange?.(canonical);
  }, [canonical, onCanonicalNodesChange]);
  const cbc = useCbcConnection(props.projectId);
  const connected = cbc.data?.configured === true;
  const validated = connected && !!cbc.data?.last_login_at;
  const [cbcDialogOpen, setCbcDialogOpen] = useState(false);
  const [details, setDetails] = useState<OrgTreeNode | null>(null);
  const [opening, setOpening] = useState<string | null>(null);
  const openingController = useRef<AbortController | null>(null);
  useEffect(() => () => openingController.current?.abort(), []);
  const play = async (row: OrgUnitRow) => {
    if (opening || props.runBlockedReason || !connected) return;
    setOpening(row.key);
    const abort = new AbortController();
    openingController.current = abort;
    try {
      const context = await getOrgUnitContext(props.projectId, row.key);
      const node = orgNodeFromContext(context);
      if (!context.parent_registered) {
        toast.error(t("Register the parent first, or use Run all."));
        return;
      }
      if (abort.signal.aborted) return;
      props.onRunStart?.(node);
      const mode = props.resolveScriptMode
        ? await props.resolveScriptMode({
            node,
            unit: context.unit,
            ancestors: context.ancestors,
            signal: abort.signal,
          })
        : "auto";
      if (mode === null) return;
      const opened = await openOrgUnitConversation(
        props.projectId,
        context.unit,
        context.ancestors,
        mode,
      );
      if (abort.signal.aborted) return;
      const owner = await resolveOrgResumedOwner(props.projectId, node, opened);
      if (abort.signal.aborted) return;
      if (!sameOrgUnit(owner, node)) {
        toast.info(
          otherConversationOpenText(
            t,
            owner ? effectiveOrgId(owner) : opened.activityCode,
          ),
        );
        if (owner) props.onConversationOpened?.(opened.runId, owner);
        return;
      }
      props.onConversationOpened?.(opened.runId, node);
      props.onRunStateChange?.();
    } catch (error) {
      toastOpenError(t, error, "org_structure", props.projectId);
    } finally {
      setOpening(null);
    }
  };
  const item = (row: OrgUnitRow, depth: number): ExecutionListRow => {
    const status = props.statusByKey?.get(row.key);
    const state = status?.status ?? row.migration_status ?? "pending";
    const states: Record<string, ExecutionRowStatus> = {
      completed: "done",
      failed: "failed",
      interrupted: "interrupted",
      running: "running",
      awaiting_human_decision: "waiting",
    };
    return {
      key: row.key,
      code: row.target_id ?? row.id,
      originalCode:
        row.target_id && row.target_id !== row.id ? row.id : undefined,
      name: row.name ?? "—",
      typeLabel: t(orgUnitTypeLabelKey(undefined, row.level_name), {
        defaultValue: row.level_name,
      }),
      depth,
      status: props.waitingRunIds?.has(status?.last_run_id ?? "")
        ? "waiting"
        : (states[state] ?? "idle"),
      testId: `org-execution-${row.key}`,
      statusLabel:
        row.level_code === "company" && state === "pending"
          ? t("Created with its Company Code")
          : undefined,
      run:
        row.level_code === "company" ||
        state === "completed" ||
        state === "awaiting_human_decision"
          ? null
          : {
              label: t("Run {{code}}", { code: row.target_id ?? row.id }),
              title: props.runBlockedReason ?? t("Run"),
              disabled: !!props.runBlockedReason || !connected,
              busy: opening === row.key || state === "running",
              onRun: () => void play(row),
            },
      details: {
        label: t("Details of {{code}}", { code: row.id }),
        onDetails: () =>
          void getOrgUnitContext(props.projectId, row.key).then((context) =>
            setDetails({
              ...orgNodeFromContext(context),
              lastRunId: status?.last_run_id ?? row.last_run_id ?? null,
            }),
          ),
      },
    };
  };
  const renderRows = (rows: OrgUnitRow[], depth: number) =>
    rows.map((row) => (
      <OrgExecutionBranch
        key={`${q}:${row.key}`}
        row={row}
        depth={depth}
        projectId={props.projectId}
        flat={!!q.trim()}
        register={register}
        renderRows={renderRows}
        renderRow={() => (
          <ExecutionLine
            row={item(row, depth)}
            selected={props.activeNodeKey === row.key}
            onSelect={() =>
              props.onSelectNodeOverride?.(
                canonical.find((node) => node.key === row.key) ??
                  orgNodeFromRow(row),
              )
            }
          />
        )}
      />
    ));
  return (
    <>
      <ExecutionListCard
        overline={t("APPROVED IN MANAGEMENT")}
        title={t("Organizational structure")}
        done={props.counts?.completed ?? 0}
        total={props.counts?.approved ?? 0}
        search={search}
        onSearchChange={setSearch}
        onSearchEnter={commitSearch}
        rows={[]}
        onSelect={() => {}}
        connection={
          validated
            ? {
                tone: "ok",
                label: t("Connected to CBC"),
                url: cbc.data?.base_url,
                openLabel: t("Open CBC in a new tab"),
              }
            : connected
              ? {
                  tone: "neutral",
                  label: t("CBC configured · connection not tested"),
                  url: cbc.data?.base_url,
                  openLabel: t("Open CBC in a new tab"),
                }
              : {
                  tone: "warn",
                  label: t("CBC not configured"),
                  configureLabel: t("Configure the CBC connection"),
                  onConfigure: () => setCbcDialogOpen(true),
                }
        }
        toolbarActions={
          props.runQueue?.kind === "running" ? (
            <>
              <span role="status">
                {t("Unit {{current}} of {{total}}", {
                  current: props.runQueue.current,
                  total: props.runQueue.total,
                })}
              </span>
              <button onClick={props.onStopQueue}>{t("Stop queue")}</button>
            </>
          ) : (
            <RunAllButton
              label={t("Run all")}
              disabledReason={
                props.runBlockedReason ??
                (!connected
                  ? t("CBC connection required to run the agent")
                  : null)
              }
              onClick={() => props.onRunAll?.([])}
            />
          )
        }
        notice={
          props.runQueue?.kind === "stopped" ? (
            <p role="status">
              {t("Queue stopped at {{code}}", { code: props.runQueue.code })}
            </p>
          ) : undefined
        }
        body={
          roots.isPending ? (
            <StageLoading variant="list" />
          ) : roots.isError && !roots.data ? (
            <div role="alert">
              <button onClick={() => void roots.refetch()}>{t("Retry")}</button>
            </div>
          ) : (
            <>
              <ul>{renderRows(rootRows, 0)}</ul>
              <LoadMore
                hasMore={!!roots.hasNextPage}
                loading={roots.isFetchingNextPage}
                onLoadMore={() => void roots.fetchNextPage()}
              />
            </>
          )
        }
      />
      <CbcConnectionDialog
        projectId={props.projectId}
        open={cbcDialogOpen}
        onOpenChange={setCbcDialogOpen}
      />
      <OrgNodeDetailDrawer
        node={details}
        onClose={() => setDetails(null)}
        hideCrud
        hideExecution
      />
    </>
  );
}
function OrgExecutionBranch({
  row,
  depth,
  projectId,
  flat,
  register,
  renderRow,
  renderRows,
}: {
  row: OrgUnitRow;
  depth: number;
  projectId: string;
  flat: boolean;
  register: (key: string, rows: OrgUnitRow[]) => void;
  renderRow: () => React.ReactNode;
  renderRows: (rows: OrgUnitRow[], depth: number) => React.ReactNode;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const children = useOrgUnitsScroll(
    projectId,
    undefined,
    { verdict: "approved", parent: row.key },
    open && !flat,
  );
  const rows = useMemo(
    () => children.data?.pages.flatMap((page) => page.items) ?? [],
    [children.data],
  );
  useEffect(() => {
    register(row.key, rows);
  }, [row.key, rows, register]);
  return (
    <li>
      <div className="relative">
        {row.child_count > 0 && !flat && (
          <button
            type="button"
            aria-label={t(open ? "Collapse {{name}}" : "Expand {{name}}", {
              name: row.target_id ?? row.id,
            })}
            aria-expanded={open}
            onClick={() => setOpen((value) => !value)}
            className="absolute left-0 top-3 z-10"
          >
            {open ? "−" : "+"}
          </button>
        )}
        <ul>{renderRow()}</ul>
      </div>
      {open && !flat && (
        <>
          {children.isPending ? (
            <StageLoading variant="panel" />
          ) : (
            <ul>{renderRows(rows, depth + 1)}</ul>
          )}
          <LoadMore
            hasMore={!!children.hasNextPage}
            loading={children.isFetchingNextPage}
            onLoadMore={() => void children.fetchNextPage()}
          />
        </>
      )}
    </li>
  );
}
